import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Edit, Save, X, ClipboardList, Award, Eye, CheckCircle2, XCircle,
  ShieldCheck, Check, Hourglass, MailCheck, MailX, Sparkles, Star, Crown, User, TrendingUp, Users2,
} from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

const EMPTY_Q = { text: "", options: ["", "", "", ""], correct_index: 0, explanation: "", order: 0 };

// Colori e icone per il grado potenziale nella card operatore
const RANK_STYLES = {
  "Vice Sceriffo":            { color: "text-[#D4C480]",   glow: "shadow-[#D4C480]/20", ring: "border-[#8B7B49]/60",   bg: "bg-[#8B7B49]/10",  icon: User },
  "Vice Sceriffo Anziano":    { color: "text-sheriff-gold",glow: "shadow-sheriff-gold/25", ring: "border-sheriff-gold/60", bg: "bg-sheriff-gold/10", icon: Star },
  "Sergente":                 { color: "text-emerald-300", glow: "shadow-emerald-500/30", ring: "border-emerald-500/60", bg: "bg-emerald-500/10", icon: TrendingUp },
  "Tenente":                  { color: "text-cyan-300",    glow: "shadow-cyan-500/30",    ring: "border-cyan-500/60",    bg: "bg-cyan-500/10",    icon: Users2 },
  "Capitano":                 { color: "text-blue-300",    glow: "shadow-blue-500/30",    ring: "border-blue-500/60",    bg: "bg-blue-500/10",    icon: ShieldCheck },
  "Capo Divisione":           { color: "text-purple-300",  glow: "shadow-purple-500/30",  ring: "border-purple-500/60",  bg: "bg-purple-500/10",  icon: Crown },
  "Assistente Sceriffo":      { color: "text-rose-300",    glow: "shadow-rose-500/30",    ring: "border-rose-500/60",    bg: "bg-rose-500/10",    icon: Sparkles },
};
function rankStyle(name) { return RANK_STYLES[name] || RANK_STYLES["Vice Sceriffo"]; }

/**
 * AssessmentAdminPanel — HR:
 *  Tab 1: Autorizzazioni (richieste operatori: approva/rifiuta)
 *  Tab 2: Tentativi (card fancy con grado potenziale + apri dettaglio)
 *  Tab 3: Domande (CRUD)
 */
export default function AssessmentAdminPanel() {
  const [tab, setTab] = useState("auth");
  const [authList, setAuthList] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [decisionDialog, setDecisionDialog] = useState(null); // { id, status, current_note }

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [auth, a, q] = await Promise.all([
        api.get("/assessment/auth/admin"),
        api.get("/assessment/attempts"),
        api.get("/assessment/admin/questions"),
      ]);
      setAuthList(auth.data?.items || []);
      setAttempts(a.data?.items || []);
      setQuestions(q.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const openAttempt = async (id) => {
    setDetail({ id, loading: true });
    try {
      const { data } = await api.get(`/assessment/attempts/${id}`);
      setDetail(data);
    } catch (e) {
      setDetail(null);
      toast.error(e?.response?.data?.detail || "Errore caricamento dettaglio");
    }
  };

  const deleteAttempt = async (id) => {
    if (!window.confirm("Eliminare questo tentativo?")) return;
    try { await api.delete(`/assessment/attempts/${id}`); toast.success("Tentativo eliminato"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const deleteQuestion = async (id) => {
    if (!window.confirm("Eliminare questa domanda?")) return;
    try { await api.delete(`/assessment/admin/questions/${id}`); toast.success("Domanda eliminata"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const saveQuestion = async () => {
    if (!editing?.text?.trim() || editing.options.some((o) => !o.trim())) {
      toast.error("Compila testo domanda e tutte le opzioni"); return;
    }
    setSaving(true);
    try {
      const payload = {
        text: editing.text.trim(),
        options: editing.options.map((o) => o.trim()),
        correct_index: Number(editing.correct_index),
        explanation: editing.explanation || "",
        order: Number(editing.order || 0),
      };
      if (editing.id) { await api.put(`/assessment/admin/questions/${editing.id}`, payload); toast.success("Domanda aggiornata"); }
      else { await api.post("/assessment/admin/questions", payload); toast.success("Domanda creata"); }
      setEditing(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio fallito"); }
    finally { setSaving(false); }
  };

  const decideAuth = async (id, status, hr_note) => {
    try {
      await api.patch(`/assessment/auth/admin/${id}`, { status, hr_note: hr_note || "" });
      toast.success(status === "approved" ? "Richiesta approvata" : "Richiesta rifiutata");
      setDecisionDialog(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore decisione");
    }
  };

  const deleteAuth = async (id) => {
    if (!window.confirm("Eliminare questa richiesta di autorizzazione?")) return;
    try { await api.delete(`/assessment/auth/admin/${id}`); toast.success("Richiesta rimossa"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const pendingCount = authList.filter((a) => a.status === "pending").length;
  const approvedCount = authList.filter((a) => a.status === "approved").length;

  return (
    <div className="space-y-6" data-testid="assessment-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="uppercase-label text-sheriff-gold flex items-center gap-2"><ClipboardList className="h-4 w-4" /> Quiz Valutazione</div>
          <h2 className="font-heading font-black text-2xl text-[#F5E6B8] mt-1">Gestione Quiz Grado</h2>
          <p className="text-sm text-[#B8A269]">Autorizzazioni operatori, storico tentativi con grado potenziale e gestione domande.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <TabBtn active={tab === "auth"} onClick={() => setTab("auth")} testId="assessment-tab-auth" badge={pendingCount} badgeTone="amber">
            Autorizzazioni ({authList.length})
          </TabBtn>
          <TabBtn active={tab === "attempts"} onClick={() => setTab("attempts")} testId="assessment-tab-attempts">
            Tentativi ({attempts.length})
          </TabBtn>
          <TabBtn active={tab === "questions"} onClick={() => setTab("questions")} testId="assessment-tab-questions">
            Domande ({questions.length})
          </TabBtn>
        </div>
      </div>

      {loading && <div className="text-center py-8 text-[#B8A269]"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>}

      {/* ==================== TAB AUTORIZZAZIONI ==================== */}
      {!loading && tab === "auth" && (
        <div className="space-y-3" data-testid="assessment-auth-list">
          {pendingCount === 0 && approvedCount === 0 && authList.length === 0 ? (
            <div className="bg-[#0F2620] border border-sheriff-gold/25 rounded-2xl p-10 text-center text-[#8B7B49] italic">
              Nessuna richiesta di autorizzazione.
            </div>
          ) : (
            <>
              {pendingCount > 0 && (
                <div className="rounded-2xl border border-amber-500/40 bg-amber-500/8 p-3">
                  <div className="uppercase-label text-amber-200 text-[10px] flex items-center gap-2 mb-2"><Hourglass className="h-3.5 w-3.5" /> In sospeso ({pendingCount})</div>
                  <div className="space-y-2">
                    {authList.filter((a) => a.status === "pending").map((a) => (
                      <AuthRow key={a.id} req={a} onApprove={() => setDecisionDialog({ id: a.id, status: "approved", user: a })} onReject={() => setDecisionDialog({ id: a.id, status: "rejected", user: a })} onDelete={() => deleteAuth(a.id)} />
                    ))}
                  </div>
                </div>
              )}
              {authList.filter((a) => a.status !== "pending").length > 0 && (
                <div className="rounded-2xl border border-sheriff-gold/20 bg-[#0F2620] p-3">
                  <div className="uppercase-label text-sheriff-gold/70 text-[10px] mb-2">Storico decisioni</div>
                  <div className="space-y-2">
                    {authList.filter((a) => a.status !== "pending").map((a) => (
                      <AuthRow key={a.id} req={a} onDelete={() => deleteAuth(a.id)} readonly />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ==================== TAB TENTATIVI (fancy) ==================== */}
      {!loading && tab === "attempts" && (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="assessment-attempts-grid">
          {attempts.length === 0 ? (
            <div className="col-span-full bg-[#0F2620] border border-sheriff-gold/25 rounded-2xl p-10 text-center text-[#8B7B49] italic">
              Nessun tentativo ancora sostenuto.
            </div>
          ) : attempts.map((a) => {
            const s = rankStyle(a.suggested_rank);
            const RankIcon = s.icon;
            const pct = a.total ? Math.round((a.score / a.total) * 100) : 0;
            return (
              <div
                key={a.id}
                className={`relative overflow-hidden rounded-2xl border-2 ${s.ring} bg-gradient-to-br from-[#143830] via-[#0F2620] to-[#0A1F18] p-5 shadow-lg ${s.glow} transition-all hover:-translate-y-0.5 cursor-pointer`}
                onClick={() => openAttempt(a.id)}
                data-testid={`attempt-row-${a.id}`}
              >
                <div className={`absolute -top-14 -right-14 h-40 w-40 rounded-full ${s.bg} blur-3xl pointer-events-none`} />
                <div className="relative flex items-start gap-3 mb-3">
                  <div className={`h-12 w-12 rounded-xl ${s.bg} ${s.ring} border flex items-center justify-center shrink-0`}>
                    <RankIcon className={`h-6 w-6 ${s.color}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="uppercase-label text-sheriff-gold/70 text-[9px]">Operatore</div>
                    <div className="font-heading font-bold text-[#F5E6B8] truncate">{a.user_rp_name || a.user_email}</div>
                    <div className="text-[10px] text-[#8B7B49] font-mono truncate">{a.user_role || "—"} · {a.user_email}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button onClick={(e) => { e.stopPropagation(); openAttempt(a.id); }} variant="ghost" size="sm" className="text-sheriff-gold hover:bg-sheriff-gold/10 h-8 w-8 p-0" data-testid={`attempt-view-${a.id}`} title="Vedi risposte">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button onClick={(e) => { e.stopPropagation(); deleteAttempt(a.id); }} variant="ghost" size="sm" className="text-rose-300 hover:bg-rose-500/15 h-8 w-8 p-0" data-testid={`attempt-delete-${a.id}`} title="Elimina">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className={`relative rounded-xl ${s.bg} ${s.ring} border px-4 py-3 mb-3`}>
                  <div className="uppercase-label text-sheriff-gold/70 text-[9px] mb-0.5">Grado potenziale</div>
                  <div className={`font-heading font-black text-xl ${s.color} leading-tight tracking-tight`}>
                    {a.suggested_rank || "—"}
                  </div>
                </div>

                <div className="relative flex items-baseline justify-between gap-3">
                  <div>
                    <div className="uppercase-label text-sheriff-gold/70 text-[9px]">Punteggio</div>
                    <div className="font-mono font-black text-2xl text-sheriff-gold">
                      {a.score}<span className="text-sm text-[#B8A269]">/{a.total}</span>
                      <span className="ml-2 text-sm text-emerald-300 font-bold">{pct}%</span>
                    </div>
                  </div>
                  <div className="text-right text-[10px] text-[#8B7B49] font-mono">
                    {a.at ? new Date(a.at).toLocaleString("it-IT") : "—"}
                  </div>
                </div>
                <div className="relative mt-3 h-1.5 bg-[#143830] rounded-full overflow-hidden">
                  <div className={`h-full ${s.color.replace("text-", "bg-")} transition-all`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ==================== TAB DOMANDE (CRUD) ==================== */}
      {!loading && tab === "questions" && (
        <>
          <div className="flex justify-end">
            <Button onClick={() => setEditing({ ...EMPTY_Q })} data-testid="assessment-new-question" className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] rounded-full uppercase-label">
              <Plus className="h-4 w-4 mr-2" /> Nuova domanda
            </Button>
          </div>
          {editing && (
            <div className="bg-[#0F2620] border border-sheriff-gold/40 rounded-2xl p-5 space-y-3" data-testid="assessment-editor">
              <div>
                <label className="uppercase-label text-[10px] text-sheriff-gold mb-1 block">Testo domanda</label>
                <Textarea rows={3} value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} className="bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8]" data-testid="editor-text" />
              </div>
              <div>
                <label className="uppercase-label text-[10px] text-sheriff-gold mb-1 block">Opzioni (segna la corretta)</label>
                {editing.options.map((opt, i) => (
                  <div key={i} className="flex items-center gap-2 mb-1.5">
                    <input type="radio" checked={editing.correct_index === i} onChange={() => setEditing({ ...editing, correct_index: i })} className="accent-emerald-500" data-testid={`editor-correct-${i}`} />
                    <span className="text-sheriff-gold text-xs uppercase w-6">{String.fromCharCode(65 + i)}</span>
                    <Input value={opt} onChange={(e) => { const opts = [...editing.options]; opts[i] = e.target.value; setEditing({ ...editing, options: opts }); }} className="flex-1 bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8]" data-testid={`editor-opt-${i}`} />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="uppercase-label text-[10px] text-sheriff-gold mb-1 block">Ordine</label>
                  <Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} className="bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8]" />
                </div>
              </div>
              <div>
                <label className="uppercase-label text-[10px] text-sheriff-gold mb-1 block">Spiegazione (opzionale)</label>
                <Textarea rows={2} value={editing.explanation} onChange={(e) => setEditing({ ...editing, explanation: e.target.value })} className="bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8]" />
              </div>
              <div className="flex justify-end gap-2">
                <Button onClick={() => setEditing(null)} variant="ghost" className="text-[#D4C480]" disabled={saving}><X className="h-4 w-4 mr-1" /> Annulla</Button>
                <Button onClick={saveQuestion} disabled={saving} className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18]" data-testid="editor-save">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Salva domanda
                </Button>
              </div>
            </div>
          )}
          <div className="space-y-2">
            {questions.length === 0 ? (
              <div className="text-center py-8 text-[#8B7B49] italic">Nessuna domanda. Clicca &quot;Nuova domanda&quot; per iniziare.</div>
            ) : questions.map((q) => (
              <div key={q.id} className="bg-[#0F2620] border border-sheriff-gold/20 rounded-xl p-4" data-testid={`question-row-${q.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] text-sheriff-gold/60 uppercase tracking-widest mb-1">Ordine {q.order}</div>
                    <div className="text-[#F5E6B8] font-medium mb-1">{q.text}</div>
                    <div className="text-xs text-[#B8A269]">
                      {q.options.map((o, i) => (
                        <span key={i} className={`inline-block mr-3 ${i === q.correct_index ? "text-emerald-300 font-bold" : ""}`}>
                          {String.fromCharCode(65 + i)}: {o} {i === q.correct_index && "✓"}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button onClick={() => setEditing({ ...q })} variant="ghost" size="sm" className="text-sheriff-gold hover:bg-sheriff-gold/10 h-8 w-8 p-0" data-testid={`question-edit-${q.id}`}><Edit className="h-4 w-4" /></Button>
                    <Button onClick={() => deleteQuestion(q.id)} variant="ghost" size="sm" className="text-rose-300 hover:bg-rose-500/15 h-8 w-8 p-0" data-testid={`question-delete-${q.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <AttemptDetailDialog attempt={detail} onClose={() => setDetail(null)} />
      <AuthDecisionDialog dec={decisionDialog} onCancel={() => setDecisionDialog(null)} onConfirm={(note) => decideAuth(decisionDialog.id, decisionDialog.status, note)} />
    </div>
  );
}

function TabBtn({ active, onClick, testId, children, badge, badgeTone }) {
  const badgeCls = badgeTone === "amber" ? "bg-amber-500/20 text-amber-200 border-amber-500/40" : "bg-sheriff-gold/20 text-sheriff-gold border-sheriff-gold/40";
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`relative px-4 py-2 rounded-full uppercase-label text-xs border transition-colors ${active ? "bg-sheriff-gold/20 border-sheriff-gold text-sheriff-gold" : "border-sheriff-gold/30 text-[#B8A269] hover:border-sheriff-gold/60"}`}
    >
      {children}
      {badge > 0 && (
        <span className={`absolute -top-1.5 -right-1.5 px-1.5 py-0.5 rounded-full text-[9px] font-mono font-black border ${badgeCls}`}>{badge}</span>
      )}
    </button>
  );
}

function AuthRow({ req, onApprove, onReject, onDelete, readonly }) {
  const statusMap = {
    pending:  { label: "IN ATTESA", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40", Icon: Hourglass },
    approved: { label: "APPROVATA", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", Icon: MailCheck },
    rejected: { label: "RIFIUTATA", cls: "bg-rose-500/15 text-rose-300 border-rose-500/40", Icon: MailX },
    used:     { label: "UTILIZZATA", cls: "bg-sheriff-gold/15 text-sheriff-gold border-sheriff-gold/40", Icon: Check },
  };
  const meta = statusMap[req.status] || statusMap.pending;
  const StatusIcon = meta.Icon;
  return (
    <div className="bg-[#0A1F18]/70 border border-sheriff-gold/20 rounded-xl p-3 flex flex-wrap items-start gap-3" data-testid={`auth-row-${req.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[#F5E6B8] font-medium truncate">{req.user_rp_name || req.user_email}</div>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold border uppercase tracking-[0.15em] ${meta.cls}`}>
            <StatusIcon className="h-3 w-3" /> {meta.label}
          </span>
        </div>
        <div className="text-[10px] text-[#8B7B49] font-mono mt-0.5">
          {req.user_role || "—"} · {req.user_email} · richiesta {req.requested_at ? new Date(req.requested_at).toLocaleString("it-IT") : "—"}
        </div>
        {req.motivazione && (
          <div className="text-xs text-[#D4C480] italic mt-1">&ldquo;{req.motivazione}&rdquo;</div>
        )}
        {req.hr_note && (
          <div className="text-[11px] text-sheriff-gold/70 mt-1 border-t border-sheriff-gold/10 pt-1">
            Nota HR: {req.hr_note}
            {req.decided_by_name && <span className="text-[#8B7B49] font-mono ml-2">· {req.decided_by_name}</span>}
          </div>
        )}
      </div>
      <div className="flex gap-1.5 shrink-0">
        {!readonly && (
          <>
            <Button onClick={onApprove} size="sm" data-testid={`auth-approve-${req.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full uppercase-label text-[10px] h-8">
              <Check className="h-3.5 w-3.5 mr-1" /> Approva
            </Button>
            <Button onClick={onReject} size="sm" variant="outline" data-testid={`auth-reject-${req.id}`} className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 rounded-full uppercase-label text-[10px] h-8">
              <X className="h-3.5 w-3.5 mr-1" /> Rifiuta
            </Button>
          </>
        )}
        <Button onClick={onDelete} size="sm" variant="ghost" data-testid={`auth-delete-${req.id}`} className="text-rose-300 hover:bg-rose-500/15 h-8 w-8 p-0">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function AuthDecisionDialog({ dec, onCancel, onConfirm }) {
  const [note, setNote] = useState("");
  useEffect(() => { setNote(""); }, [dec?.id]);
  const isApprove = dec?.status === "approved";
  return (
    <Dialog open={!!dec} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-md bg-[#143830] border-sheriff-gold/40 text-[#F5E6B8] rounded-2xl" data-testid="auth-decision-dialog">
        <DialogHeader>
          <div className="uppercase-label text-sheriff-gold mb-1 flex items-center gap-2">
            {isApprove ? <MailCheck className="h-4 w-4" /> : <MailX className="h-4 w-4" />} Decisione richiesta
          </div>
          <DialogTitle className="font-heading text-xl text-[#F5E6B8]">
            {isApprove ? "Approva autorizzazione" : "Rifiuta autorizzazione"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#D4C480]">
            {isApprove
              ? "L'operatore potrà iniziare il quiz appena caricato."
              : "L'operatore verrà avvisato del rifiuto e potrà ripresentare la richiesta."}
          </p>
          <div>
            <label className="uppercase-label text-[10px] text-sheriff-gold mb-1 block">Nota (opzionale, mostrata all&apos;operatore)</label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8]" data-testid="auth-decision-note" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel} className="border-sheriff-gold/30 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full uppercase-label" data-testid="auth-decision-cancel">
            Annulla
          </Button>
          <Button
            onClick={() => onConfirm(note.trim())}
            data-testid="auth-decision-confirm"
            className={`rounded-full uppercase-label font-bold ${isApprove ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-rose-600 hover:bg-rose-700 text-white"}`}
          >
            {isApprove ? "Conferma approvazione" : "Conferma rifiuto"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AttemptDetailDialog({ attempt, onClose }) {
  const open = !!attempt;
  const ready = open && attempt && !attempt.loading && Array.isArray(attempt.details);
  const s = ready ? rankStyle(attempt.suggested_rank) : null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl bg-gradient-to-br from-[#143830] to-[#0A1F18] border-sheriff-gold/40 text-[#F5E6B8] rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="assessment-attempt-detail">
        <DialogHeader>
          <div className="uppercase-label text-sheriff-gold mb-1">Dettaglio Quiz Valutazione</div>
          {ready ? (
            <>
              <DialogTitle className="font-heading font-black text-2xl text-[#F5E6B8]">
                {attempt.user_rp_name || attempt.user_email}
                <span className="ml-3 font-mono text-sheriff-gold">{attempt.score}/{attempt.total} · {attempt.percent}%</span>
              </DialogTitle>
              <div className={`mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${s.ring} ${s.bg}`}>
                <Award className={`h-4 w-4 ${s.color}`} />
                <span className="uppercase-label text-[10px] text-sheriff-gold/70">Grado potenziale</span>
                <span className={`font-heading font-bold ${s.color}`}>{attempt.suggested_rank}</span>
              </div>
              <div className="text-xs text-[#B8A269] font-mono mt-2">
                {attempt.at ? new Date(attempt.at).toLocaleString("it-IT") : "—"}
              </div>
            </>
          ) : (
            <DialogTitle className="font-heading text-xl text-[#F5E6B8]">Caricamento…</DialogTitle>
          )}
        </DialogHeader>
        {open && attempt?.loading && (
          <div className="py-8 text-center text-[#B8A269]"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        )}
        {ready && (
          <div className="space-y-3 pt-2">
            {attempt.details.map((d, i) => {
              const borderCls = d.ok ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/5";
              const Icon = d.ok ? CheckCircle2 : XCircle;
              const iconCls = d.ok ? "text-emerald-400" : "text-rose-400";
              return (
                <div key={d.question_id || i} className={`p-4 rounded-xl border ${borderCls}`} data-testid={`attempt-detail-q-${i}`}>
                  <div className="flex items-start gap-2">
                    <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${iconCls}`} />
                    <div className="flex-1">
                      <div className="font-mono text-[10px] uppercase tracking-[0.18em] font-bold text-sheriff-gold/60 mb-1 flex items-center gap-2 flex-wrap">
                        <span>Domanda {i + 1}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[9px] ${d.ok ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"}`}>
                          {d.ok ? "CORRETTA" : "SBAGLIATA"}
                        </span>
                      </div>
                      <div className="font-semibold text-[#F5E6B8] mb-2">{d.text}</div>
                      <div className="text-xs text-[#B8A269]">
                        Risposta data:{" "}
                        <span className={`font-mono ${d.ok ? "text-emerald-300" : "text-rose-300"}`}>
                          {d.chosen_index != null && d.chosen_text != null ? `${String.fromCharCode(65 + d.chosen_index)}) ${d.chosen_text}` : "— nessuna —"}
                        </span>
                      </div>
                      {!d.ok && (
                        <div className="text-xs text-emerald-300 mt-0.5">
                          Risposta corretta:{" "}
                          <span className="font-mono">
                            {d.correct_index != null && d.correct_text != null ? `${String.fromCharCode(65 + d.correct_index)}) ${d.correct_text}` : "—"}
                          </span>
                        </div>
                      )}
                      {d.explanation && (
                        <div className="mt-2 text-[11px] text-sheriff-gold/70 italic border-t border-sheriff-gold/15 pt-2">Nota: {d.explanation}</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
